import {
	ADD_TO_CART_PARTNUMBER_PROMOTION_DETAILS_KEY,
	PROMOTION_DETAILS_KEY,
} from 'presentation/events/services/promotionsService';

import { CartPromotionCookies, PromotionCookies } from './types';

export const safeParseArray = (value?: string): any[] => {
	if (!value) return [];

	try {
		return Array.isArray(value) ? value : [];
	} catch {
		console.error(`Error parsing value as array: ${value}`);
		return [];
	}
};

export const safeSetSessionItem = (key: string, value: unknown) => {
	try {
		if (typeof window === 'undefined') return;
		if (!window.sessionStorage) return;

		sessionStorage.setItem(key, JSON.stringify(value));
	} catch {
		console.error(`Error saving ${key} to session storage`);
	}
};

export const safeGetSessionItem = (key: string): Record<string, unknown> | null => {
	try {
		if (typeof window === 'undefined') return null;
		if (!window.sessionStorage) return null;

		const raw = sessionStorage.getItem(key);
		if (!raw) return null;

		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : null;
	} catch {
		console.error(`Error reading ${key} from session storage`);
		return null;
	}
};

const buildPromotionFromCookies = (cookies: PromotionCookies) => {
	const existing = safeGetSessionItem(PROMOTION_DETAILS_KEY);
	const existingPartnumbers = Array.isArray(existing?.list_partnumbers)
		? existing?.list_partnumbers
		: [];
	const existingKeyWords = Array.isArray(existing?.key_words) ? existing?.key_words : [];

	return {
		list_partnumbers: existingPartnumbers,
		key_words: existingKeyWords,
		promotion_name: cookies.promotion_name ?? 'NA',
		promotion_id: cookies.promotion_id ?? 'NA',
		creative_name: cookies.creative_name ?? 'NA',
		creative_slot: cookies.creative_slot ?? 'NA',
	};
};

const stripWrappingQuotes = (word: string): string => word.replace(/^["']|["']$/g, '').trim();

export const parseKeyWordsCookie = (value: unknown): string[] => {
	if (Array.isArray(value)) {
		return value
			.filter((word): word is string => typeof word === 'string')
			.map((word) => word.trim())
			.filter((word) => word !== '');
	}

	if (typeof value !== 'string') return [];

	return value
		.replace(/^\s*\[/, '')
		.replace(/\]\s*$/, '')
		.split(',')
		.map((word) => stripWrappingQuotes(word.trim()))
		.filter((word) => word !== '');
};

export const syncKeyWordsFromCookie = (keyWords?: unknown) => {
	const parsed = parseKeyWordsCookie(keyWords);
	if (parsed.length === 0) return;

	try {
		const existing = safeGetSessionItem(PROMOTION_DETAILS_KEY) ?? {};
		const updated = { ...existing, key_words: parsed };

		safeSetSessionItem(PROMOTION_DETAILS_KEY, updated);
	} catch {
		console.error('[MOBILE] - Error saving key_words to session storage');
	}
};

export const syncPromotionDetailsFromCookies = (cookies: PromotionCookies) => {
	const hasPromotionCookies =
		cookies.promotion_id ||
		cookies.promotion_name ||
		cookies.creative_name ||
		cookies.creative_slot;

	if (!hasPromotionCookies) return;

	try {
		safeSetSessionItem(PROMOTION_DETAILS_KEY, buildPromotionFromCookies(cookies));
	} catch {
		console.error('[MOBILE] - Error saving promDtls to session storage');
	}
};

const normalizePartNumber = (partNumbers: unknown): string => {
	if (typeof partNumbers === 'string') return partNumbers.trim();
	if (Array.isArray(partNumbers)) return String(partNumbers[0] ?? '').trim();
	return '';
};

export const syncCartPromotionDetailsFromCookies = (cookies: CartPromotionCookies) => {
	if (!cookies.promoCartDtls) return;

	try {
		const promotionsInCart = safeParseArray(cookies.promoCartDtls)?.map((item) => ({
			SKU: normalizePartNumber(item?.partNumbers),
			promotion_name: item?.promotionName ?? 'NA',
			promotion_id: item?.promotionId ?? 'NA',
			creative_name: item?.creativeName ?? 'NA',
			creative_slot: Number.isFinite(Number(item?.creativeSlot)) ? Number(item.creativeSlot) : 0,
		}));

		safeSetSessionItem(ADD_TO_CART_PARTNUMBER_PROMOTION_DETAILS_KEY, promotionsInCart);
	} catch {
		console.error('[MOBILE] - Error saving promoCartDtls to session storage');
	}
};
